import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Check, ChevronLeft, ChevronRight, Clock, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import apiClient, { SERVICES_META } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "service", label: "Service" },
  { key: "schedule", label: "Date & Time" },
  { key: "addons", label: "Add-ons" },
  { key: "details", label: "Your Details" },
  { key: "review", label: "Review & Pay" },
];

const TIMES = ["12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Booking() {
  const navigate = useNavigate();
  const query = useQuery();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    service: query.get("service") || "photo_booth",
    event_date: null,
    start_time: "18:00",
    hours: 4,
    prints: 0,
    themed_props: false,
    theme: "",
    full_name: "",
    email: "",
    phone: "",
    event_type: "",
    venue: "",
    notes: "",
  });

  const [quote, setQuote] = useState({ total: 350, deposit: 175, balance: 175, discount: 0, breakdown: { base: 350, extra_hours: 0, prints: 0, themed_props: 0 } });
  const quoteSeqRef = React.useRef(0);
  const [bookedDatesByService, setBookedDatesByService] = useState({});
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/bookings/booked-dates")
      .then((r) => {
        if (!cancelled) setBookedDatesByService(r.data.by_service || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const bookedDatesForService = useMemo(
    () => new Set(bookedDatesByService[form.service] || []),
    [bookedDatesByService, form.service]
  );

  useEffect(() => {
    const seq = ++quoteSeqRef.current;
    apiClient
      .post("/bookings/quote", {
        service: form.service,
        hours: form.hours,
        prints: form.prints,
        themed_props: form.themed_props,
        promo_code: appliedPromo || undefined,
      })
      .then((r) => {
        if (seq === quoteSeqRef.current) setQuote(r.data);
      })
      .catch((err) => {
        if (seq !== quoteSeqRef.current) return;
        if (appliedPromo) {
          setAppliedPromo(null);
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Promo code no longer valid");
        }
      });
  }, [form.service, form.hours, form.prints, form.themed_props, appliedPromo]);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setApplyingPromo(true);
    try {
      const { data } = await apiClient.post("/bookings/quote", {
        service: form.service,
        hours: form.hours,
        prints: form.prints,
        themed_props: form.themed_props,
        promo_code: code,
      });
      quoteSeqRef.current += 1;
      setQuote(data);
      setAppliedPromo(data.promo_code);
      toast.success(`Promo ${data.promo_code} applied — you save $${data.discount.toFixed(2)}!`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
  };

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canAdvance = useMemo(() => {
    switch (STEPS[step].key) {
      case "service":
        return !!form.service;
      case "schedule":
        return !!form.event_date && !!form.start_time && form.hours >= 4;
      case "addons":
        return true;
      case "details":
        return form.full_name && form.email && form.phone && form.event_type && form.venue;
      default:
        return true;
    }
  }, [step, form]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        promo_code: appliedPromo || null,
        event_date: form.event_date
          ? new Date(form.event_date.getTime() - form.event_date.getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 10)
          : null,
      };
      const { data: booking } = await apiClient.post("/bookings", payload);
      const { data: checkout } = await apiClient.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="booking-page">
      <Navbar />
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main flow */}
          <div className="lg:col-span-8">
            <div className="mb-12">
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Reserve Your Date</span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light mt-3">
                Let's plan your <span className="italic gold-gradient-text">moment.</span>
              </h1>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-2 md:gap-4 mb-12 overflow-x-auto" data-testid="booking-stepper">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 shrink-0">
                  <div
                    className={`w-7 h-7 flex items-center justify-center text-xs border ${
                      i < step
                        ? "bg-[#D4AF37] border-[#D4AF37] text-[#050505]"
                        : i === step
                        ? "border-[#D4AF37] text-[#D4AF37]"
                        : "border-zinc-700 text-zinc-600"
                    }`}
                  >
                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-xs uppercase tracking-widest ${i === step ? "text-white" : "text-zinc-500"}`}>
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && <span className="text-zinc-700 mx-1">—</span>}
                </div>
              ))}
            </div>

            {/* Step content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={STEPS[step].key}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.35 }}
                className="min-h-[320px]"
              >
                {STEPS[step].key === "service" && (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {Object.entries(SERVICES_META).map(([key, svc]) => (
                      <button
                        key={key}
                        type="button"
                        data-testid={`select-service-${key}`}
                        onClick={() => update({ service: key })}
                        className={`text-left lk-card overflow-hidden ${
                          form.service === key ? "border-[#D4AF37]" : ""
                        }`}
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-6">
                          <h3 className="font-serif text-2xl mb-1">{svc.title}</h3>
                          <p className="text-xs uppercase tracking-widest text-[#D4AF37]">From $350 / 4 hrs</p>
                          <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{svc.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {STEPS[step].key === "schedule" && (
                  <div className="grid md:grid-cols-2 gap-10">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-4 block">Event Date</label>
                      <div className="lk-card p-4 inline-block">
                        <Calendar
                          mode="single"
                          selected={form.event_date}
                          onSelect={(d) => update({ event_date: d })}
                          disabled={(d) => {
                            if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                            return bookedDatesForService.has(iso);
                          }}
                          data-testid="booking-calendar"
                        />
                      </div>
                      {bookedDatesForService.size > 0 && (
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">
                          Dimmed dates are already booked for {SERVICES_META[form.service]?.title || "this service"}. The other booth may still be available.
                        </p>
                      )}
                    </div>
                    <div className="space-y-8">
                      <div>
                        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-4 block">Start Time</label>
                        <div className="grid grid-cols-3 gap-2">
                          {TIMES.map((t) => (
                            <button
                              key={t}
                              type="button"
                              data-testid={`time-${t}`}
                              onClick={() => update({ start_time: t })}
                              className={`text-sm py-3 border transition-colors ${
                                form.start_time === t
                                  ? "border-[#D4AF37] bg-[#D4AF37] text-[#050505]"
                                  : "border-[#D4AF37]/20 text-zinc-300 hover:border-[#D4AF37]/60"
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-4 block">Duration</label>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            data-testid="hours-decrement"
                            onClick={() => update({ hours: Math.max(4, form.hours - 1) })}
                            className="w-10 h-10 border border-[#D4AF37]/30 hover:border-[#D4AF37]"
                          >
                            −
                          </button>
                          <div className="font-serif text-3xl gold-gradient-text w-20 text-center" data-testid="hours-value">
                            {form.hours}h
                          </div>
                          <button
                            type="button"
                            data-testid="hours-increment"
                            onClick={() => update({ hours: Math.min(12, form.hours + 1) })}
                            className="w-10 h-10 border border-[#D4AF37]/30 hover:border-[#D4AF37]"
                          >
                            +
                          </button>
                          <div className="text-xs text-zinc-500 ml-2">
                            <Clock className="w-3 h-3 inline mr-1" /> 4 hr minimum, +$50 per extra hour
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {STEPS[step].key === "addons" && (
                  <div className="space-y-10 max-w-2xl">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Printed Photos ($3 / print)</label>
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={form.prints}
                        onChange={(e) => update({ prints: parseInt(e.target.value || "0", 10) })}
                        className="lk-input"
                        data-testid="prints-input"
                      />
                      <p className="text-xs text-zinc-500 mt-2">On-site instant prints your guests take home.</p>
                    </div>

                    <div className="lk-card p-6 flex items-start justify-between gap-6">
                      <div>
                        <h4 className="font-serif text-xl mb-1">Themed Props (+$50)</h4>
                        <p className="text-sm text-zinc-400 mb-3">Curated to match your event theme.</p>
                        {form.themed_props && (
                          <input
                            type="text"
                            placeholder="e.g. Masquerade, Hollywood, Tropical..."
                            value={form.theme}
                            onChange={(e) => update({ theme: e.target.value })}
                            className="lk-input mt-2"
                            data-testid="theme-input"
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid="toggle-props"
                        onClick={() => update({ themed_props: !form.themed_props })}
                        className={`shrink-0 w-12 h-7 border ${
                          form.themed_props ? "bg-[#D4AF37] border-[#D4AF37]" : "border-zinc-600"
                        } relative transition-colors`}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 transition-all ${
                            form.themed_props ? "left-6 bg-[#050505]" : "left-0.5 bg-zinc-400"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {STEPS[step].key === "details" && (
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6 max-w-3xl">
                    <Field label="Full Name" testid="full-name" value={form.full_name} onChange={(v) => update({ full_name: v })} />
                    <Field label="Email" testid="email" type="email" value={form.email} onChange={(v) => update({ email: v })} />
                    <Field label="Phone" testid="phone" value={form.phone} onChange={(v) => update({ phone: v })} />
                    <Field label="Event Type" testid="event-type" value={form.event_type} placeholder="Wedding, Birthday, Corporate..." onChange={(v) => update({ event_type: v })} />
                    <div className="md:col-span-2">
                      <Field label="Venue / Address" testid="venue" value={form.venue} onChange={(v) => update({ venue: v })} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Notes (optional)</label>
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={(e) => update({ notes: e.target.value })}
                        className="lk-input resize-none"
                        data-testid="notes-input"
                      />
                    </div>
                  </div>
                )}

                {STEPS[step].key === "review" && (
                  <ReviewStep form={form} quote={quote} />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav buttons */}
            <div className="mt-14 flex items-center justify-between">
              <button
                type="button"
                onClick={prev}
                disabled={step === 0}
                data-testid="booking-back"
                className="text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white disabled:opacity-30 flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={next}
                  disabled={!canAdvance}
                  data-testid="booking-next"
                  className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? "Redirecting..." : "Pay Deposit & Confirm"}
                </button>
              )}
            </div>
          </div>

          {/* Summary card */}
          <aside className="lg:col-span-4">
            <div className="lk-card p-8 sticky top-28" data-testid="booking-summary">
              <h3 className="font-serif text-2xl mb-6">Your Reservation</h3>
              <Row label="Service" value={SERVICES_META[form.service]?.title || "—"} />
              <Row
                label="Date"
                value={form.event_date ? form.event_date.toDateString() : "Select a date"}
              />
              <Row label="Start" value={form.start_time || "—"} />
              <Row label="Hours" value={`${form.hours}h`} />
              <Row label="Prints" value={form.prints} />
              <Row label="Themed Props" value={form.themed_props ? "Yes" : "No"} />

              <div className="border-t border-[#D4AF37]/15 my-6" />
              <div className="space-y-2 text-sm">
                <Row mini label="Base (4hr)" value={`$${quote.breakdown.base.toFixed(2)}`} />
                <Row mini label="Extra hours" value={`$${quote.breakdown.extra_hours.toFixed(2)}`} />
                <Row mini label="Prints" value={`$${quote.breakdown.prints.toFixed(2)}`} />
                <Row mini label="Themed props" value={`$${quote.breakdown.themed_props.toFixed(2)}`} />
              </div>
              <div className="border-t border-[#D4AF37]/15 my-6" />

              {/* Promo code */}
              {appliedPromo ? (
                <div className="flex items-center justify-between mb-4 text-sm" data-testid="applied-promo">
                  <span className="text-[#D4AF37] tracking-widest uppercase text-xs">Promo: {appliedPromo}</span>
                  <button type="button" onClick={removePromo} className="text-xs text-zinc-500 hover:text-red-400 uppercase tracking-widest" data-testid="remove-promo">
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="Promo code"
                    className="lk-input flex-1 text-sm"
                    data-testid="promo-input"
                  />
                  <button
                    type="button"
                    onClick={applyPromo}
                    disabled={applyingPromo || !promoInput.trim()}
                    className="lk-btn-secondary text-xs px-4 disabled:opacity-40"
                    data-testid="apply-promo"
                  >
                    {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}
              {quote.discount > 0 && (
                <div className="flex justify-between text-sm mb-3" data-testid="discount-row">
                  <span className="text-[#D4AF37]">Discount</span>
                  <span className="text-[#D4AF37]">−${quote.discount.toFixed(2)}</span>
                </div>
              )}

              <div className="flex items-end justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-zinc-400">Event Total</span>
                <span className="font-serif text-3xl text-white" data-testid="quote-total">
                  ${quote.total.toFixed(2)}
                </span>
              </div>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 mt-4">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Due Today (50% Deposit)</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Non-refundable after 5 days</div>
                  </div>
                  <span className="font-serif text-3xl gold-gradient-text" data-testid="quote-deposit">
                    ${(quote.deposit ?? quote.total / 2).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-zinc-400">
                <span className="uppercase tracking-widest">Balance at event</span>
                <span data-testid="quote-balance">${(quote.balance ?? quote.total / 2).toFixed(2)}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
                Secure deposit via Stripe holds your date instantly. Balance due on or before your event day. Deposits are
                refundable only if you cancel within 5 days of booking.
              </p>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="lk-input"
        data-testid={`field-${testid}`}
      />
    </div>
  );
}

function Row({ label, value, mini }) {
  return (
    <div className={`flex justify-between ${mini ? "text-zinc-400 text-sm" : "py-1.5 text-zinc-300"}`}>
      <span className={mini ? "" : "text-xs uppercase tracking-widest text-zinc-500"}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ReviewStep({ form, quote }) {
  const deposit = quote.deposit ?? quote.total / 2;
  const balance = quote.balance ?? quote.total / 2;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="review-step">
      <p className="text-zinc-400 leading-relaxed">
        Confirm the details below. Clicking <strong className="text-[#D4AF37]">Pay Deposit &amp; Confirm</strong> takes you to
        Stripe to pay your 50% non-refundable deposit. The remaining balance is due on or before your event day.
      </p>
      <div className="lk-card p-6 space-y-3 text-sm">
        <Row label="Name" value={form.full_name} />
        <Row label="Email" value={form.email} />
        <Row label="Phone" value={form.phone} />
        <Row label="Event" value={form.event_type} />
        <Row label="Venue" value={form.venue} />
        {form.notes && <Row label="Notes" value={form.notes} />}
      </div>
      <div className="lk-card p-6 grid sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Event Total</div>
          <div className="font-serif text-2xl text-white mt-1">${quote.total.toFixed(2)}</div>
        </div>
        <div className="sm:border-l sm:border-[#D4AF37]/15 sm:pl-4">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Due Today (50%)</div>
          <div className="font-serif text-2xl gold-gradient-text mt-1">${deposit.toFixed(2)}</div>
        </div>
        <div className="sm:border-l sm:border-[#D4AF37]/15 sm:pl-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Balance at event</div>
          <div className="font-serif text-2xl text-white mt-1">${balance.toFixed(2)}</div>
        </div>
      </div>
      <div className="text-xs text-zinc-500 leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4">
        <strong className="text-zinc-300">Cancellation policy:</strong> Deposit is fully refundable if you cancel within 5 days
        of booking. After that, the 50% deposit becomes non-refundable.
      </div>
    </div>
  );
}
